import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Button,
  Stack,
  Avatar,
  Tooltip,
} from "@mui/material";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import GroupsIcon from "@mui/icons-material/Groups";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import { useNavigate } from "react-router-dom";
import { apiData } from "../../services/api_service";
import { fetchSalesTargetDashboardWidgetAPI } from "../../config";
import { formatNumber, formatAED } from "../../utils/format";

const ShinySkeleton = ({ width, height, borderRadius = 8, style = {} }) => (
  <Box
    sx={{
      width: width || "100%",
      height: height || 20,
      borderRadius: typeof borderRadius === "number" ? `${borderRadius}px` : borderRadius,
      background: "linear-gradient(90deg, #e2e8f0 0%, #f8fafc 35%, #ffffff 50%, #f8fafc 65%, #e2e8f0 100%)",
      backgroundSize: "400% 100%",
      animation: "shinyShimmer 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite",
      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
      "@keyframes shinyShimmer": {
        "0%": { backgroundPosition: "100% 0%" },
        "100%": { backgroundPosition: "0% 0%" },
      },
      ...style,
    }}
  />
);

const SalesTargetWidget = ({
  salesPersonId = null,
  storeId = null,
  fromDate = null,
  toDate = null,
  period = null,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [widgetData, setWidgetData] = useState(null);

  const fetchWidgetData = async () => {
    try {
      setLoading(true);
      const payload = {
        ...(salesPersonId && Number(salesPersonId) > 0 && { sales_person_id: salesPersonId }),
        ...(storeId && Number(storeId) > 0 && { store_id: storeId }),
        ...(fromDate && { from_date: fromDate }),
        ...(toDate && { to_date: toDate }),
        ...(period && { period }),
      };
      const res = await apiData("get", fetchSalesTargetDashboardWidgetAPI, payload);
      const data = res?.data?.data || res?.data || null;
      setWidgetData(data);
    } catch (err) {
      console.error("SalesTargetWidget fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWidgetData();
  }, [salesPersonId, storeId, fromDate, toDate, period]);

  if (loading) {
    return <ShinySkeleton height={145} borderRadius={12} style={{ marginBottom: 24 }} />;
  }

  if (!widgetData) return null;

  const {
    month_name,
    year,
    days_remaining,
    target_amount,
    actual_sales,
    paid_collections,
    actual_orders,
    achievement_percentage,
    shortfall,
    surplus,
    daily_required_run_rate,
    status,
    target_source,
    inherited_period,
    sales_person_name,
    total_sales_persons,
    persons_with_target,
    achieved_count,
    on_track_count,
    lagging_count,
  } = widgetData;

  const pct = achievement_percentage || 0;
  const noTarget = status === "No Target";
  const statusColor = noTarget ? "#64748b" : status === "Achieved" ? "#16a34a" : status === "On Track" ? "#eab308" : "#dc2626";
  const statusBg = noTarget ? "#f8fafc" : status === "Achieved" ? "#f0fdf4" : status === "On Track" ? "#fefce8" : "#fff5f5";
  const statusBorder = noTarget ? "#e2e8f0" : status === "Achieved" ? "#bbf7d0" : status === "On Track" ? "#fef08a" : "#fecaca";

  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: 3,
        bgcolor: "#ffffff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
        transition: "all 0.2s ease",
        "&:hover": { boxShadow: "0 6px 20px rgba(15,23,42,0.06)", borderColor: "#cbd5e1" },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Grid container spacing={2.5} alignItems="center">
          {/* Section 1: Target Header & Progress Bar (Left Column) */}
          <Grid item xs={12} md={5}>
            <Stack direction="row" spacing={1.5} alignItems="center" mb={1.2}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  bgcolor: "#4f46e5",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <TrackChangesIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box minWidth={0} flex={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                  Monthly Target ({month_name} {year})
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight="500">
                  Filter Context: <strong style={{ color: "#334155" }}>{sales_person_name}</strong>
                  {target_source === "team_aggregate" && (
                    <span style={{ color: "#64748b" }}> — {persons_with_target || 0} of {total_sales_persons || 0} sales reps have a target</span>
                  )}
                </Typography>
              </Box>
              <Chip
                label={
                  target_source === "explicit"
                    ? "Target Set"
                    : target_source === "inherited"
                    ? `Past (${inherited_period})`
                    : target_source === "team_aggregate"
                    ? "Team Aggregate"
                    : "Default Target"
                }
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.65rem", height: 20, fontWeight: 700 }}
              />
            </Stack>

            <Box sx={{ mt: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={0.6}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {noTarget ? "Sales" : "Achieved"}: <strong style={{ color: "#0284c7", fontWeight: 800 }}>{formatAED(actual_sales)}</strong>
                </Typography>
                <Chip
                  label={noTarget ? "No Target" : `${pct}% (${status})`}
                  size="small"
                  sx={{
                    bgcolor: statusBg,
                    color: statusColor,
                    fontWeight: 800,
                    fontSize: "0.72rem",
                    height: 22,
                    border: `1px solid ${statusBorder}`,
                  }}
                />
              </Stack>

              <LinearProgress
                variant="determinate"
                value={Math.min(100, pct)}
                sx={{
                  display: noTarget ? "none" : "block",
                  height: 9,
                  borderRadius: 5,
                  bgcolor: "#e2e8f0",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: statusColor,
                    borderRadius: 5,
                  },
                }}
              />

              <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.8}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Target Revenue: <strong>{noTarget ? "Not set" : `${formatAED(target_amount)}`}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Paid Collections: <strong style={{ color: "#16a34a" }}>{formatAED(paid_collections)}</strong>
                </Typography>
              </Stack>
            </Box>
          </Grid>

          {/* Section 2: Detailed KPI Metrics Grid (Middle Column) */}
          <Grid item xs={12} sm={7} md={4}>
            <Box
              sx={{
                p: 1.8,
                bgcolor: "#f8fafc",
                borderRadius: 2.5,
                border: "1px solid #e2e8f0",
              }}
            >
              <Grid container spacing={1.2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="#64748b" fontWeight="600" textTransform="uppercase" letterSpacing="0.3px" display="block">
                    Shortfall / Surplus
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: noTarget ? "#64748b" : shortfall > 0 ? "#dc2626" : "#16a34a", fontSize: "0.9rem" }}>
                    {noTarget ? "—" : shortfall > 0 ? `- ${formatAED(shortfall)}` : `Surplus: +${formatAED(surplus)} 🎯`}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="#64748b" fontWeight="600" textTransform="uppercase" letterSpacing="0.3px" display="block">
                    Total Orders
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.9rem" }}>
                    {actual_orders} Orders
                  </Typography>
                </Grid>

                {noTarget ? (
                  <Grid item xs={12} sx={{ pt: 1, borderTop: "1px dashed #cbd5e1", mt: 0.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="#64748b" fontWeight="700">
                        No targets set for this month
                      </Typography>
                      <Typography variant="caption" color="#64748b" fontWeight="600">
                        Days Remaining: <strong style={{ color: "#0f172a" }}>{days_remaining} Days</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                ) : shortfall > 0 && days_remaining > 0 ? (
                  <Grid item xs={12} sx={{ pt: 1, borderTop: "1px dashed #cbd5e1", mt: 0.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="#64748b" fontWeight="600">
                        Run Rate Req: <strong style={{ color: "#d97706" }}>{formatAED(daily_required_run_rate)}/day</strong>
                      </Typography>
                      <Typography variant="caption" color="#64748b" fontWeight="600">
                        Days Remaining: <strong style={{ color: "#0f172a" }}>{days_remaining} Days</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                ) : (
                  <Grid item xs={12} sx={{ pt: 1, borderTop: "1px dashed #cbd5e1", mt: 0.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="#16a34a" fontWeight="700">
                        Target Performance Achieved!
                      </Typography>
                      <Typography variant="caption" color="#64748b" fontWeight="600">
                        Days Remaining: <strong style={{ color: "#0f172a" }}>{days_remaining} Days</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>

          {/* Section 3: Team Breakdown & Direct Action Button (Right Column) */}
          <Grid item xs={12} sm={5} md={3} textAlign={{ xs: "left", md: "right" }}>
            <Box display="flex" flexDirection="column" alignItems={{ xs: "flex-start", md: "flex-end" }} gap={1.2}>
              {/* Status Breakdown Pills for Team View */}
              <Grid container spacing={0.6} sx={{ width: "100%", maxWidth: 240, display: noTarget ? "none" : "flex" }}>
                <Grid item xs={4}>
                  <Box textAlign="center" sx={{ bgcolor: "#f0fdf4", py: 0.3, px: 0.2, borderRadius: 1.5, border: "1px solid #bbf7d0" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#16a34a", fontSize: "0.8rem", lineHeight: 1.1 }}>
                      {formatNumber(achieved_count)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#15803d", fontSize: "0.58rem", fontWeight: 700, display: "block", whiteSpace: "nowrap" }}>
                      Achieved
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={4}>
                  <Box textAlign="center" sx={{ bgcolor: "#fefce8", py: 0.3, px: 0.2, borderRadius: 1.5, border: "1px solid #fef08a" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#ca8a04", fontSize: "0.8rem", lineHeight: 1.1 }}>
                      {formatNumber(on_track_count)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#a16207", fontSize: "0.58rem", fontWeight: 700, display: "block", whiteSpace: "nowrap" }}>
                      On Track
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={4}>
                  <Box textAlign="center" sx={{ bgcolor: "#fff5f5", py: 0.3, px: 0.2, borderRadius: 1.5, border: "1px solid #fecaca" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#dc2626", fontSize: "0.8rem", lineHeight: 1.1 }}>
                      {formatNumber(lagging_count)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#b91c1c", fontSize: "0.58rem", fontWeight: 700, display: "block", whiteSpace: "nowrap" }}>
                      Lagging
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Button
                variant="contained"
                color="primary"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate("/reports/sales-target-report")}
                sx={{
                  borderRadius: 2,
                  px: 2.5,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
                }}
              >
                View Target Report
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default SalesTargetWidget;
